import React, { useState, useEffect, useContext } from 'react'
import ThumbsFeedback from './ThumbsFeedback'
import MetricDisplay from './MetricDisplay'
import ActionButtons from './ActionButtons'
import CardHeader from './CardHeader'
import OpportunityDialog from '@/components/dashboard/roadmap/OpportunityDialog'
import InstanceConfigDialog from './InstanceConfigDialog'
import type { DevTool } from '@/components/dashboard/roadmap/DevToolSelect'
import {
  getStoredInstanceUrl,
  getTaskCreationUrl,
  buildOpportunityTaskDescription,
} from '@/components/utils/taskCreationUtils'
import { useToast } from '@/hooks/use-toast'
import { RoadmapContext } from '@/context/RoadmapContext'

interface DefaultOpportunityCardProps {
  number?: number
  cardId: string
  devTool: DevTool
  opportunity: any
}

const DefaultOpportunityCard = ({ number, devTool, opportunity }: DefaultOpportunityCardProps) => {
  const { meetings, toggleArchiveOpportunity } = useContext(RoadmapContext)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [configDialogOpen, setConfigDialogOpen] = useState(false)
  const [currentDevTool, setCurrentDevTool] = useState<DevTool>(devTool)
  const { toast } = useToast()

  useEffect(() => {
    const handleDevToolChange = (e: CustomEvent) => {
      if (e.detail) {
        setCurrentDevTool(e.detail as DevTool)
      }
    }

    window.addEventListener('devToolChange', handleDevToolChange as EventListener)

    return () => {
      window.removeEventListener('devToolChange', handleDevToolChange as EventListener)
    }
  }, [])

  useEffect(() => {
    if (devTool !== currentDevTool) {
      setCurrentDevTool(devTool)
    }
  }, [devTool, currentDevTool])

  const handleSendToDevTool = (e: React.MouseEvent) => {
    e.stopPropagation()

    if (currentDevTool === 'linear') {
      window.open(
        getTaskCreationUrl(
          currentDevTool,
          '',
          opportunity.title,
          buildOpportunityTaskDescription(opportunity),
          opportunity.dataPoints
        ),
        '_blank'
      )

      toast({
        title: 'Link opened',
        description: `Creating a new task in ${currentDevTool.charAt(0).toUpperCase() + currentDevTool.slice(1)}`,
      })
      return
    }

    const savedInstanceUrl = getStoredInstanceUrl(currentDevTool)

    if (!savedInstanceUrl) {
      setConfigDialogOpen(true)
    } else {
      if (currentDevTool === 'productboard') {
        setConfigDialogOpen(true)
      } else {
        window.open(
          getTaskCreationUrl(
            currentDevTool,
            savedInstanceUrl,
            opportunity.title,
            buildOpportunityTaskDescription(opportunity),
            opportunity.dataPoints
          ),
          '_blank'
        )

        toast({
          title: 'Link opened',
          description: `Creating a new task in ${currentDevTool.charAt(0).toUpperCase() + currentDevTool.slice(1)}`,
        })
      }
    }
  }

  const handleArchive = async (e: React.MouseEvent) => {
    e.stopPropagation()

    await toggleArchiveOpportunity(opportunity.id)

    toast({
      title: opportunity.isArchived ? 'Card restored' : 'Card archived',
      description: opportunity.isArchived
        ? 'The card has been restored successfully'
        : 'The card has been archived successfully',
    })
  }

  const evidence = Array.isArray(opportunity.Evidence) ? opportunity.Evidence : []
  let evidenceDistinctMeetings = [...new Set(evidence.map((evidence: any) => evidence.meeting_id))]
  evidenceDistinctMeetings = evidenceDistinctMeetings.filter((meeting) => meeting)
  const evidenceDistinctMeetingsCount = evidenceDistinctMeetings.length

  // Count VIP meetings among evidenceDistinctMeetings
  const vipMeetingsCount = evidenceDistinctMeetings.reduce<number>((count, meetingId) => {
    const meeting = meetings.find((m: any) => m.id === meetingId)
    return meeting && meeting.is_vip ? count + 1 : count
  }, 0)

  return (
    <>
      <div
        onClick={() => setIsDialogOpen(true)}
        className="bg-white dark:bg-gray-900 rounded-lg p-5 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer border border-gray-200 dark:border-gray-700 min-h-[180px] flex flex-col relative"
      >
        <div className="flex-1">
          <CardHeader number={number} />

          <div className="mb-3">
            <h3 className="font-manrope text-[15px] font-bold text-gray-900 dark:text-gray-100 leading-tight">
              {opportunity.title}
            </h3>
          </div>
          <p className="font-manrope text-sm text-gray-600 dark:text-gray-400 mb-4 min-h-[36px] line-clamp-2">
            {opportunity.subtitle}
          </p>

          <div className="flex items-center justify-between mt-auto">
            <div className="flex items-center justify-between w-full">
              <MetricDisplay
                dataPoints={evidenceDistinctMeetingsCount}
                percentage={Math.round((evidenceDistinctMeetingsCount / meetings.length) * 100)}
                vipCount={vipMeetingsCount}
              />
              <ThumbsFeedback />
            </div>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <ActionButtons
            onSendToDevTool={handleSendToDevTool}
            onArchive={handleArchive}
            devTool={currentDevTool}
            isArchived={opportunity.isArchived}
          />
        </div>
      </div>

      <OpportunityDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        opportunity={opportunity}
        number={number}
        onSendToDevTool={handleSendToDevTool}
        devTool={currentDevTool}
      />

      <InstanceConfigDialog
        isOpen={configDialogOpen}
        onClose={() => setConfigDialogOpen(false)}
        devTool={currentDevTool}
        title={opportunity.title}
        description={opportunity.description}
        dataPoints={opportunity.dataPoints}
        opportunity={opportunity}
      />
    </>
  )
}

export default DefaultOpportunityCard
